import { memo, useCallback, useRef, useState, useEffect, type JSX } from 'react'

import { SmepWorkspace } from '@nativewaves/nw-cloud-sdk/smep'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table, SelectionToolbar } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, WorkspaceDialogContent, RowActions, SelectionToolbarActions } from 'domains/Workspaces'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { workspacesOverviewRoute } from 'pages/NWPlatform/Workspaces/Routing'
import { workspaceRoute } from 'pages/NWPlatform/Workspaces/Workspace'
import { workspaceQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: workspacesOverviewRoute.fullPath })
  const { environmentId } = workspacesOverviewRoute.useParams()
  const { query } = workspacesOverviewRoute.useSearch()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const tableInstanceRef = useRef<ReactTableType<SmepWorkspace>>(undefined)

  const workspacesQuery = useInfiniteQuery({
    ...workspaceQueryKeys.list({ environmentId, query }),
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })
  const workspaces = workspacesQuery.data?.flat

  const columns = useColumns()

  const infinityProps = {
    hasNextPage: workspacesQuery.hasNextPage,
    isFetchingNextPage: workspacesQuery.isFetchingNextPage,
    fetchNextPage: workspacesQuery.fetchNextPage,
  }

  useEffect(() => {
    if (workspacesQuery.isSuccess) {
      tableInstanceRef.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = workspacesQuery.data.flat.find((task) => task.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [workspacesQuery.data?.flat, workspacesQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.searchWorkspace')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.list({ environmentId }).queryKey,
              }),
            [environmentId, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(tableInstance) => void (tableInstanceRef.current = tableInstance)}
        size={56}
        headSize={48}
        data={workspaces ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspacesQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={workspacesOverviewRoute.fullPath}
            to={workspaceRoute.to}
            params={{ workspaceId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', '_goToAvcTasks']}
        enableInfinity
        infinityProps={infinityProps}
        ActionMenuProps={(row) => ({ title: row.original.name, entity: t('entity:smep.workspace', { count: 1 }) })}
        RowActions={(props) => <RowActions {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:smep.workspace', { count: 2 })}
            discardSelection={() => tableInstanceRef.current?.toggleAllRowsSelected(false)}
            Actions={
              tableInstanceRef.current && (
                <SelectionToolbarActions selectedRows={selectedRows} tableInstance={tableInstanceRef.current} />
              )
            }
          />
        )}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <WorkspaceDialogContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
